import { EditOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import {
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { useSaveSetting, useSettings } from '../api/hooks';
import { extractErrorMessage } from '../api/client';
import { toJsonText } from '../utils/format';

const { Title, Text } = Typography;

interface Row {
  key: string;
  value: unknown;
}

/** Cấu hình giới hạn tần suất (rate limit) — lưu Setting `rateLimit`, áp dụng ngay khi lưu. */
function RateLimitCard() {
  const { message } = AntApp.useApp();
  const settings = useSettings();
  const save = useSaveSetting();
  const [form] = Form.useForm();

  const cfg = (settings.data?.rateLimit as any) || {};
  useEffect(() => {
    form.setFieldsValue({
      enabled: cfg.enabled !== false,
      limit: typeof cfg.limit === 'number' ? cfg.limit : 100,
      ttlSec: typeof cfg.ttlSec === 'number' ? cfg.ttlSec : typeof cfg.ttlMs === 'number' ? cfg.ttlMs / 1000 : 60,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  const onSave = async () => {
    const v = await form.validateFields();
    try {
      await save.mutateAsync({
        key: 'rateLimit',
        value: { enabled: !!v.enabled, limit: Math.floor(Number(v.limit)), ttlSec: Math.floor(Number(v.ttlSec)) },
      });
      message.success('Đã lưu giới hạn tần suất (áp dụng ngay)');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <Card size="small" style={{ marginBottom: 16 }} title="Giới hạn tần suất (Rate limit)">
      <Text type="secondary">
        Giới hạn số request/khoảng thời gian cho mỗi IP trên API công khai. Tắt = không giới hạn.
        Thay đổi áp dụng ngay (không cần khởi động lại). Riêng đăng nhập admin luôn giữ giới hạn riêng.
      </Text>
      <Form form={form} layout="inline" style={{ marginTop: 12, rowGap: 12 }}>
        <Form.Item name="enabled" label="Bật" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="limit" label="Số request tối đa">
          <InputNumber min={1} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="ttlSec" label="Trong khoảng (giây)">
          <InputNumber min={1} style={{ width: 140 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={onSave}>
            Lưu
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

/** Cấu hình TTL kho media tạm — lưu Setting `mediaStore`, áp dụng ngay khi lưu. */
function MediaStoreCard() {
  const { message } = AntApp.useApp();
  const settings = useSettings();
  const save = useSaveSetting();
  const [form] = Form.useForm();

  const cfg = (settings.data?.mediaStore as any) || {};
  useEffect(() => {
    form.setFieldsValue({
      ttlHours: typeof cfg.ttlHours === 'number' ? cfg.ttlHours : typeof cfg.ttlMs === 'number' ? cfg.ttlMs / 3600000 : 24,
      inputTtlHours: typeof cfg.inputTtlHours === 'number' ? cfg.inputTtlHours : 120, // 5 ngày
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.data]);

  const onSave = async () => {
    const v = await form.validateFields();
    try {
      await save.mutateAsync({
        key: 'mediaStore',
        value: { ttlHours: Number(v.ttlHours), inputTtlHours: Number(v.inputTtlHours) },
      });
      message.success('Đã lưu thời gian lưu media tạm (áp dụng ngay)');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  return (
    <Card size="small" style={{ marginBottom: 16 }} title="Media lưu tạm">
      <Text type="secondary">
        Ảnh output được lưu thành URL gateway để trả về API/webhook. Ảnh/video input đính kèm ở trang kiểm thử tự xóa theo
        thời gian riêng. File quá hạn được dọn định kỳ để giải phóng dung lượng.
      </Text>
      <Form form={form} layout="inline" style={{ marginTop: 12, rowGap: 12 }}>
        <Form.Item name="ttlHours" label="Output lưu (giờ)" rules={[{ required: true, message: 'Vui lòng nhập số giờ' }]}>
          <InputNumber min={1} precision={2} style={{ width: 130 }} />
        </Form.Item>
        <Form.Item
          name="inputTtlHours"
          label="Input lưu (giờ)"
          tooltip="Ảnh/video đính kèm trang kiểm thử tự xóa sau ngần này giờ (mặc định 120 = 5 ngày)."
          rules={[{ required: true, message: 'Vui lòng nhập số giờ' }]}
        >
          <InputNumber min={1} precision={2} style={{ width: 130 }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" icon={<SaveOutlined />} loading={save.isPending} onClick={onSave}>
            Lưu
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

export default function SettingsPage() {
  const { message } = AntApp.useApp();
  const settings = useSettings();
  const save = useSaveSetting();

  const [open, setOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [form] = Form.useForm();

  const rows: Row[] = Object.entries(settings.data ?? {}).map(([key, value]) => ({
    key,
    value,
  }));

  const openCreate = () => {
    setEditingKey(null);
    form.resetFields();
    setOpen(true);
  };

  const openEdit = (r: Row) => {
    setEditingKey(r.key);
    form.setFieldsValue({
      key: r.key,
      value:
        typeof r.value === 'string' ? r.value : toJsonText(r.value),
    });
    setOpen(true);
  };

  const onSubmit = async () => {
    const v = await form.validateFields();
    // Cố gắng parse JSON; nếu không phải JSON thì giữ nguyên chuỗi.
    let value: unknown = v.value;
    const trimmed = String(v.value ?? '').trim();
    if (trimmed) {
      try {
        value = JSON.parse(trimmed);
      } catch {
        value = v.value;
      }
    }
    try {
      await save.mutateAsync({ key: v.key, value });
      message.success('Đã lưu cài đặt');
      setOpen(false);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const columns = [
    { title: 'Khóa', dataIndex: 'key', key: 'key', width: 260 },
    {
      title: 'Giá trị',
      dataIndex: 'value',
      key: 'value',
      render: (v: unknown) => (
        <Text code style={{ fontSize: 12 }}>
          {typeof v === 'string' ? v : toJsonText(v)}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, r: Row) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
      ),
    },
  ];

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          Cài đặt
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Thêm / Sửa cài đặt
        </Button>
      </Space>

      <RateLimitCard />
      <MediaStoreCard />

      <Table<Row>
        rowKey="key"
        loading={settings.isLoading}
        dataSource={rows}
        columns={columns}
        pagination={false}
      />

      <Modal
        title={editingKey ? `Sửa: ${editingKey}` : 'Thêm cài đặt'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={onSubmit}
        confirmLoading={save.isPending}
        okText="Lưu"
        cancelText="Hủy"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Khóa"
            rules={[{ required: true, message: 'Vui lòng nhập khóa' }]}
          >
            <Input disabled={!!editingKey} />
          </Form.Item>
          <Form.Item
            name="value"
            label="Giá trị (văn bản hoặc JSON)"
            rules={[{ required: true, message: 'Vui lòng nhập giá trị' }]}
          >
            <Input.TextArea rows={5} style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
